import { fetchAuthSession } from 'aws-amplify/auth';
import { HOST_URL } from '../constants/globals';

/**
 * Enhanced API client with automatic authentication and error handling
 */
export class ApiClient {
  constructor(baseURL = HOST_URL) {
    this.baseURL = baseURL;
  }

  /**
   * Get authentication token from current session
   */
  static async getAuthToken() {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.idToken?.toString();
    } catch (error) {
      console.warn('Failed to get auth token:', error);
      return null;
    }
  }

  /**
   * Build full URL from endpoint
   */
  buildUrl(endpoint) {
    if (endpoint.startsWith('http')) {
      return endpoint;
    }
    return `${this.baseURL}${endpoint.startsWith('/') ? endpoint : `/${endpoint}`}`;
  }

  /**
   * Core request method with automatic token attachment and error handling
   */
  async request(endpoint, options = {}) {
    try {
      const url = this.buildUrl(endpoint);
      const token = await ApiClient.getAuthToken();

      // Prepare headers
      const headers = {
        'Content-Type': 'application/json',
        ...options.headers,
      };

      // Add Authorization header if token exists
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      // Make the request
      const response = await fetch(url, {
        ...options,
        headers,
      });

      // Handle authentication errors
      if (response.status === 401) {
        // Token might be expired, redirect to login
        if (typeof window !== 'undefined') {
          window.location.href = '/login';
        }
        throw new Error('Authentication required');
      }

      // Handle other HTTP errors
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText || response.statusText}`);
      }

      return response;
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  /**
   * GET request
   */
  async get(endpoint, options = {}) {
    const url = ApiClient.appendQueryParams(endpoint, options);
    return this.request(url, { method: 'GET' });
  }

  static appendQueryParams(endpoint, options = {}) {
    let url = endpoint;
    if (options && Object.keys(options).length > 0) {
      const params = new URLSearchParams(options).toString();
      url += (endpoint.includes('?') ? '&' : '?') + params;
    }
    return url;
  }

  /**
   * POST request
   */
  async post(endpoint, data = null, options = {}) {
    return this.request(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : null,
      ...options,
    });
  }

  /**
   * PUT request
   */
  async put(endpoint, data = null, options = {}) {
    return this.request(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : null,
      ...options,
    });
  }

  /**
   * PATCH request
   */
  async patch(endpoint, data = null, options = {}) {
    return this.request(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : null,
      ...options,
    });
  }

  /**
   * DELETE request
   */
  async delete(endpoint, options = {}) {
    return this.request(endpoint, { method: 'DELETE', ...options });
  }

  /**
   * Helper method to get JSON response
   */
  async getJson(endpoint, options = {}) {
    const response = await this.get(endpoint, options);
    return response.json();
  }

  /**
   * Helper method to post JSON and get JSON response
   */
  async postJson(endpoint, data = null, options = {}) {
    const response = await this.post(endpoint, data, options);
    return response.json();
  }

  /**
   * Helper method to put JSON and get JSON response
   */
  async putJson(endpoint, data = null, options = {}) {
    const response = await this.put(endpoint, data, options);
    return response.json();
  }

  /**
   * Helper method to patch JSON and get JSON response
   */
  async patchJson(endpoint, data = null, options = {}) {
    const response = await this.patch(endpoint, data, options);
    return response.json();
  }
}

// Create singleton instance
const apiClient = new ApiClient();

export default apiClient;

// Export convenience methods for backward compatibility
export const apiGet = (endpoint, options = {}) => apiClient.get(endpoint, options);
export const apiPost = (endpoint, data = null, options = {}) => apiClient.post(endpoint, data, options);
export const apiPut = (endpoint, data = null, options = {}) => apiClient.put(endpoint, data, options);
export const apiDelete = (endpoint, options = {}) => apiClient.delete(endpoint, options);
export const apiPatch = (endpoint, data = null, options = {}) => apiClient.patch(endpoint, data, options);
